import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useEffect, useState } from "react";
import type { ConflictItem, ConflictResolution } from "./types";

export interface FileConflictDialogProps {
  conflict: ConflictItem | null;
  /** Total conflicts still to resolve including the current one. */
  remainingCount: number;
  /**
   * "move" (default) keeps today's wording; "copy" rephrases the
   * description so cross-scope copies don't imply the source will move.
   */
  verb?: "move" | "copy";
  onResolve: (resolution: ConflictResolution, applyToAll: boolean) => void;
  onCancelAll: () => void;
}

export function FileConflictDialog({
  conflict,
  remainingCount,
  verb = "move",
  onResolve,
  onCancelAll,
}: FileConflictDialogProps) {
  const [applyToAll, setApplyToAll] = useState(false);

  // Reset the toggle whenever a brand-new conflict is shown so the user
  // makes a deliberate choice each time.
  useEffect(() => {
    setApplyToAll(false);
  }, [conflict?.sourceName, conflict?.isFolder]);

  const open = conflict !== null;
  const isFolder = conflict?.isFolder ?? false;
  const remaining = Math.max(0, remainingCount - 1);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancelAll();
      }}
    >
      <DialogContent className="sm:max-w-xl p-8 gap-6">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-lg leading-snug">
            &ldquo;{conflict?.sourceName ?? ""}&rdquo; already exists in this
            location.
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            {isFolder ? (
              <>
                Do you want to merge the two folders&rsquo; contents, or keep
                both versions? Note that{" "}
                {verb === "copy" ? "copying" : "merging"} won&rsquo;t change any
                existing sharing settings.
              </>
            ) : (
              <>
                Do you want to replace the content of the existing file, or keep
                both versions? Note that{" "}
                {verb === "copy" ? "copying over" : "replacing the file"}{" "}
                won&rsquo;t change sharing settings.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {remaining > 0 && (
          <div className="flex items-center gap-2.5">
            <input
              id="conflict-apply-all"
              type="checkbox"
              checked={applyToAll}
              onChange={(e) => setApplyToAll(e.target.checked)}
              className="h-4 w-4 rounded text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rust/20 cursor-pointer"
            />
            <Label
              htmlFor="conflict-apply-all"
              className="text-sm font-normal cursor-pointer text-muted-foreground"
            >
              Apply to remaining {remaining} conflict
              {remaining === 1 ? "" : "s"}
            </Label>
          </div>
        )}

        <DialogFooter className="gap-2 flex-col-reverse sm:flex-row sm:justify-between sm:gap-0 pt-2">
          <Button variant="ghost" onClick={onCancelAll}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onResolve("skip", applyToAll)}
            >
              Skip
            </Button>
            {isFolder ? (
              <Button
                variant="outline"
                onClick={() => onResolve("merge", applyToAll)}
              >
                Merge
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => onResolve("replace_contents", applyToAll)}
              >
                Replace contents
              </Button>
            )}
            <Button
              autoFocus
              onClick={() => onResolve("keep_both", applyToAll)}
            >
              Keep both
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
